import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { createDraft } from '@services/partnerService';
import { partnerEvent, partnerTag } from '../analytics';
import { usePartnerAuth } from '../PartnerAuthContext';
import { isValidLocal, toStoredPhone } from '../phone';
import { Button, C, Card, ErrorNote, Field, PhoneField, Title } from '../ui';
import { useDesktop } from '../../utils/hooks/useDesktop';

// Encabezado de grupo del formulario: label corto en mayúsculas (canon label-sm de DESIGN.md),
// con una línea divisoria sutil para separar las secciones del alta.
const SectionLabel = ({ children, first }: { children: string; first?: boolean }) => (
  <div
    style={{
      fontSize: 11,
      fontWeight: 700,
      letterSpacing: '0.06em',
      textTransform: 'uppercase',
      color: C.muted,
      margin: first ? '0 0 14px' : '4px 0 14px',
      paddingTop: first ? 0 : 18,
      borderTop: first ? 'none' : `1px solid ${C.cardBorder}`,
    }}
  >
    {children}
  </div>
);

const CreateCommerce = () => {
  const { token } = usePartnerAuth();
  const navigate = useNavigate();
  const isDesktop = useDesktop();
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [phoneError, setPhoneError] = useState<string | null>(null);

  const [nombre, setNombre] = useState('');
  const [ownerNombre, setOwnerNombre] = useState('');
  const [ownerEmail, setOwnerEmail] = useState('');
  const [ownerWhatsapp, setOwnerWhatsapp] = useState(''); // 10 digitos nacionales

  const onCreate = async (e: FormEvent) => {
    e.preventDefault();
    if (!token) return;
    setError(null);

    // WhatsApp es el destino del OTP del claim: bloquea el alta si no es un numero MX valido.
    if (!isValidLocal(ownerWhatsapp)) {
      setPhoneError('Ingresa un WhatsApp válido de 10 dígitos.');
      return;
    }
    setPhoneError(null);

    setCreating(true);
    try {
      const res = await createDraft(token, {
        negocio: {
          nombre: nombre.trim(),
        },
        owner: { nombre: ownerNombre.trim(), email: ownerEmail.trim(), whatsapp: toStoredPhone(ownerWhatsapp) },
        monthly_orders: 'menos_de_100',
        product_interest: ['menu'],
      });
      partnerEvent('partner_commerce_created');
      partnerTag({ id_commerce: res.id_comercio });
      // Directo al wizard de preparación (no al detalle): recién creado, el siguiente paso SIEMPRE
      // es preparar el comercio — mandar al detalle rompía el flujo (la Guía de alta quedaba fuera
      // de vista sin scroll, el partner se sentía perdido). El detalle SÍ es el destino correcto
      // cuando se accede a un draft existente desde la lista (ahí la guía es justo lo que se busca).
      navigate(`/partner/draft/${res.id_comercio}/configurar`);
    } catch {
      partnerEvent('partner_commerce_create_error');
      setError('No se pudo crear el comercio. Revisa los datos.');
      setCreating(false);
    }
  };

  return (
    <>
      <Title sub="Crea el comercio y su suscripción FREEMIUM. Luego podrás cargar el menú y generar el enlace de activación para el dueño.">
        Nuevo comercio
      </Title>
      <Card style={{ maxWidth: 560 }}>
        <form onSubmit={onCreate}>
          <SectionLabel first>Negocio</SectionLabel>
          <Field label="Nombre del negocio" value={nombre} onChange={setNombre} required />

          <SectionLabel>Datos del dueño</SectionLabel>
          <Field label="Nombre del dueño" value={ownerNombre} onChange={setOwnerNombre} required />
          <Field label="Email del dueño" type="email" value={ownerEmail} onChange={setOwnerEmail} required />
          <PhoneField
            label="WhatsApp del dueño"
            value={ownerWhatsapp}
            onChange={(v) => {
              setOwnerWhatsapp(v);
              if (phoneError) setPhoneError(null);
            }}
            error={phoneError}
            required
          />

          {error && <ErrorNote>{error}</ErrorNote>}

          {/*
           * Jerarquía de CTAs (DESIGN.md): el primario domina y va en la posición convencional.
           * - Móvil: apilado en columna, primario ("Crear comercio") ARRIBA a todo el ancho, "Cancelar" debajo.
           * - Desktop: en línea alineado a la derecha, secundario ("Cancelar") IZQUIERDA y primario DERECHA.
           * Un único DOM sirve ambos layouts vía `order`: en móvil (columna) Crear=1/Cancelar=2;
           * en desktop (fila) Cancelar=1/Crear=2, con justify-end para anclar el par a la derecha.
           */}
          <div
            style={{
              display: 'flex',
              flexDirection: isDesktop ? 'row' : 'column',
              justifyContent: isDesktop ? 'flex-end' : undefined,
              gap: 10,
              marginTop: 22,
            }}
          >
            <Button type="submit" fullWidth={!isDesktop} loading={creating} style={{ order: isDesktop ? 2 : 1 }}>
              Crear comercio
            </Button>
            <Button
              type="button"
              variant="secondary"
              fullWidth={!isDesktop}
              onClick={() => navigate('/partner/comercios')}
              disabled={creating}
              style={{ order: isDesktop ? 1 : 2 }}
            >
              Cancelar
            </Button>
          </div>
        </form>
      </Card>
    </>
  );
};

export default CreateCommerce;
